import React from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useDocTitle } from '../components/CustomHook';
import { Link } from 'react-router-dom';
import img from '../images/acquisitions.jpg';

const Acquisitions = () => {
    useDocTitle('Acquisitions - TerraLucrum');

    return (
        <>
            <NavBar />
            <div className="bg-gray-50 min-h-screen pt-28">
                {/* Hero Section */}
                <div className=" text-white py-20" style={{ backgroundColor: '#2D3C54' }}>
                    <div className="container mx-auto px-4 text-center">
                        <h1 className="text-4xl lg:text-6xl font-bold mb-4">Acquisitions</h1>
                        <p className="text-xl lg:text-2xl mb-8">Sourcing and acquiring high-performing real estate assets</p>
                        <Link to="/contact" className="bg-white text-terra-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Invest With Us
                        </Link>
                    </div>
                </div>

                {/* Main Content */}
                <div className="container mx-auto px-4 py-16">
                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                        <div>
                            <img src={img} alt="Real Estate Acquisitions" className="w-full max-w-md mx-auto" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-terra-blue-900 mb-6">Buy Right, Every Time</h2>
                            <p className="text-lg text-gray-700 mb-6">
                                TerraLucrum identifies both off-market and on-market opportunities across income-producing
                                real estate. Through deep local relationships and disciplined research, we surface deals
                                that other investors never see and evaluate each one against conservative, realistic assumptions.
                            </p>
                            <p className="text-lg text-gray-700">
                                We underwrite for downside protection first, targeting assets that deliver durable cash flow
                                and long-term appreciation. Every acquisition is grounded in a clear business plan, so investors
                                know exactly how each property is expected to build wealth.
                            </p>
                        </div>
                    </div>

                    {/* Services Grid */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-terra-blue-900 text-center mb-12">Our Acquisition Capabilities</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Market Analysis</h3>
                                <p className="text-gray-600">We study submarket fundamentals, rent trends, and supply pipelines to target locations positioned for sustained demand and growth.</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Deal Sourcing</h3>
                                <p className="text-gray-600">Our broker network and direct-to-owner outreach give us early access to off-market and on-market properties before they hit the open market.</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Due Diligence</h3>
                                <p className="text-gray-600">We verify every detail, from title and physical condition to leases and financials, to confirm each asset performs as represented.</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Underwriting &amp; Valuation</h3>
                                <p className="text-gray-600">Conservative models stress-test cash flow, cap rates, and exit scenarios so we pay the right price with a margin of safety.</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Financing &amp; Capital</h3>
                                <p className="text-gray-600">We structure debt and equity to optimize returns while preserving flexibility, drawing on lender and investor relationships built over years.</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Negotiation &amp; Closing</h3>
                                <p className="text-gray-600">Our team negotiates favorable terms and manages the transaction through a smooth, on-time close with minimal surprises.</p>
                            </div>
                        </div>
                    </div>

                    {/* Process Section */}
                    <div className="bg-white rounded-lg p-8 shadow-lg mb-16">
                        <h2 className="text-3xl font-bold text-terra-blue-900 text-center mb-12">Our Acquisition Process</h2>
                        <div className="grid md:grid-cols-4 gap-8">
                            <div className="text-center">
                                <div className="bg-terra-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-xl font-bold">1</span>
                                </div>
                                <h3 className="font-semibold text-terra-blue-900 mb-2">Source</h3>
                                <p className="text-gray-600">Uncover off-market and on-market opportunities that fit our investment criteria</p>
                            </div>
                            <div className="text-center">
                                <div className="bg-terra-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-xl font-bold">2</span>
                                </div>
                                <h3 className="font-semibold text-terra-blue-900 mb-2">Analyze</h3>
                                <p className="text-gray-600">Evaluate market fundamentals, rent potential, and the property's income story</p>
                            </div>
                            <div className="text-center">
                                <div className="bg-terra-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-xl font-bold">3</span>
                                </div>
                                <h3 className="font-semibold text-terra-blue-900 mb-2">Underwrite</h3>
                                <p className="text-gray-600">Model conservative returns and complete rigorous due diligence on the asset</p>
                            </div>
                            <div className="text-center">
                                <div className="bg-terra-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-xl font-bold">4</span>
                                </div>
                                <h3 className="font-semibold text-terra-blue-900 mb-2">Acquire</h3>
                                <p className="text-gray-600">Structure capital, negotiate terms, and close on the property with confidence</p>
                            </div>
                        </div>
                    </div>

                    {/* CTA Section */}
                    <div className="text-center bg-terra-blue-900 text-white p-12 rounded-lg">
                        <h2 className="text-3xl font-bold mb-4">Ready to Grow Your Portfolio?</h2>
                        <p className="text-xl mb-8">Let's discuss how TerraLucrum can help you acquire assets that build lasting wealth.</p>
                        <Link to="/contact" className="bg-white text-terra-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Contact Us Today
                        </Link>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default Acquisitions;
